//./react-app/src/scenes/vnr/roi-evaluation.tsx
import Header from '../../components/Header';
import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { tokens } from '../../theme';
import RoiPerMonth from '../../components/vnr/RoiPerMonth';
import moment from 'moment/moment';
import { FilterContext } from '../../contexts/FilterContext';
import RoiPerSource from '../../components/vnr/RoiPerSource';
import RoiPerAsset from '../../components/vnr/RoiPerAsset';
import RoiPerEmailProvider from '../../components/vnr/RoiPerEmailProvider';
import PlanungsbereichNewsletter from '../../components/vnr/PlanungsbereichNewsletter';
import RoiPerCountry from '../../components/vnr/RoiPerCountry';
import RoiPerAssetType from '../../components/vnr/RoiPerAssetType';
import RoiPerAssetSector from '../../components/vnr/RoiPerAssetSector';
import RoiPerNetwork from '../../components/vnr/RoiPerNetwork';
import RoiPerVerlag from '../../components/vnr/RoiPerVerlag';
import RoiPerCustomerStatus from '../../components/vnr/RoiPerCustomerStatus';
import RoiPerWeekday from '../../components/vnr/RoiPerWeekday';
import RoiPerTheme from '../../components/vnr/RoiPerTheme';
import RoiPerEmailDomain from '../../components/vnr/RoiPerEmailDomain';
import RevenueTimeline from '../../components/vnr/RevenueTimeline';
import AnfoTimeline from '../../components/vnr/AnfoTimeline';
import AnfoPerNewsletter from '../../components/vnr/AnfoPerNewsletter';
import RoiPerMarketCap from '../../components/vnr/RoiPerMarketCap';
import DoiTimeline from '../../components/vnr/DoiTimeline';
import RoiNetworkMonthlySummary from '../../components/vnr/RoiNetworkMonthlySummary';

interface Network {
  name: string;
}

interface Theme {
  name: string;
}

interface Verlag {
  name: string;
}

const RoiEvaluationPage: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState, setFilterState } = useContext(FilterContext)!;
  const [networks, setNetworks] = useState<Network[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [verlags, setVerlags] = useState<Verlag[]>([]);
  const [originalDates, setOriginalDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  } | null>(null);

  // Initialize dates only once - set VNR-specific defaults and store originals
  useEffect(() => {
    // Store the original dates before overriding them
    const originalStartDate = filterState.startDate;
    const originalEndDate = filterState.endDate;
    setOriginalDates({
      startDate: originalStartDate,
      endDate: originalEndDate,
    });

    const desiredStartDate = moment().subtract(3, 'months').toDate();
    const desiredEndDate = moment().subtract(30, 'days').toDate();

    setFilterState((prev) => ({
      ...prev,
      startDate: desiredStartDate,
      endDate: desiredEndDate,
    }));
  }, []); // Run only once on mount

  // Separate cleanup effect that has access to originalDates state
  useEffect(() => {
    return () => {
      if (originalDates) {
        setFilterState((prev) => ({
          ...prev,
          startDate: originalDates.startDate,
          endDate: originalDates.endDate,
        }));
      }
    };
  }, [originalDates, setFilterState]); // Depend on originalDates so cleanup has the right values

  useEffect(() => {
    fetch('/api/vnr/networks')
      .then((response) => response.json())
      .then((data) => {
        setNetworks(data);
        // Set all networks as default for VNR page
        const allNetworkNames = data.map((n: Network) => n.name);
        setFilterState((prev) => ({
          ...prev,
          network: allNetworkNames,
        }));
      })
      .catch((error) => console.error('Error fetching networks:', error));
  }, []); // Run only once on mount

  useEffect(() => {
    // Verlag = vnr_lead_data.planungsbereich; leere Werte sind keine waehlbare Auspraegung
    fetch('/api/vnr/sold-leads-summary/verlags')
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) setVerlags(data.filter((v: Verlag) => v.name));
      })
      .catch((error) => console.error('Error fetching verlags:', error));
  }, []);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        // Build query params based on current filters
        const params = new URLSearchParams();

        // Add network filter if available
        const selectedNetworks = Array.isArray(filterState.network)
          ? filterState.network
          : filterState.network
            ? [filterState.network]
            : [];
        if (selectedNetworks.length > 0) {
          selectedNetworks.forEach((network) => params.append('network', network));
        }

        filterState.vnrVerlag.forEach((verlag) => params.append('verlag', verlag));

        // Add date range if available
        if (filterState.startDate && filterState.endDate) {
          params.append('start_date', filterState.startDate.toISOString().split('T')[0]);
          params.append('end_date', filterState.endDate.toISOString().split('T')[0]);
        }

        const response = await fetch(`/api/vnr/themes?${params.toString()}`);
        const data = await response.json();
        setThemes(data);

        // Clear selected themes that are no longer available
        const availableThemeNames = data.map((theme: Theme) => theme.name);
        const currentSelectedThemes = Array.isArray(filterState.theme)
          ? filterState.theme
          : filterState.theme
            ? [filterState.theme]
            : [];
        const validSelectedThemes = currentSelectedThemes.filter((themeName) =>
          availableThemeNames.includes(themeName),
        );

        // Only update if there's a change to avoid infinite loops
        if (validSelectedThemes.length !== currentSelectedThemes.length) {
          setFilterState((prev) => ({
            ...prev,
            theme: validSelectedThemes,
          }));
        }
      } catch (error) {
        console.error('Error fetching themes:', error);
      }
    };

    fetchThemes();
  }, [filterState.network, filterState.vnrVerlag, filterState.startDate, filterState.endDate]);

  const handleNetworkChange = (event: any) => {
    const value = event.target.value;
    setFilterState((prev) => ({
      ...prev,
      network: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleVerlagChange = (event: any) => {
    const value = event.target.value;
    setFilterState((prev) => ({
      ...prev,
      vnrVerlag: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleThemeChange = (event: any) => {
    const value = event.target.value;
    setFilterState((prev) => ({
      ...prev,
      theme: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const getSelectedNetworks = (): string[] => {
    if (Array.isArray(filterState.network)) {
      return filterState.network;
    }
    return filterState.network ? [filterState.network] : [];
  };

  const getSelectedThemes = (): string[] => {
    if (Array.isArray(filterState.theme)) {
      return filterState.theme;
    }
    return filterState.theme ? [filterState.theme] : [];
  };

  const getNetworkDisplayCondition = (): 'finanztrends' | 'other' | 'mixed' => {
    const selectedNetworks = getSelectedNetworks();
    const hasFinanztrends = selectedNetworks.includes('Finanztrends');
    const hasOthers = selectedNetworks.some((n) => n !== 'Finanztrends');

    if (hasFinanztrends && hasOthers) return 'mixed';
    if (hasFinanztrends) return 'finanztrends';
    return 'other';
  };
  return (
    <Box m="20px">
      <Box justifyContent="space-between" alignItems="center" display="flex">
        <Header
          title="ROI Auswertung"
          subtitle="Das Enddatum ist automatisch um 30 Tage zurückgesetzt um einen sauberen ROI Wert abbilden zu können"
        />
        <Box sx={{ display: 'flex', gap: 2, minWidth: 1050 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="network-select-label">Netzwerk</InputLabel>
            <Select
              labelId="network-select-label"
              id="network-select"
              multiple
              value={getSelectedNetworks()}
              onChange={handleNetworkChange}
              input={<OutlinedInput id="select-multiple-chip" label="Netzwerk" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Array.isArray(networks) &&
                networks.map((network) => (
                  <MenuItem key={network.name} value={network.name}>
                    {network.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="verlag-select-label">Verlag</InputLabel>
            <Select
              labelId="verlag-select-label"
              id="verlag-select"
              multiple
              value={filterState.vnrVerlag}
              onChange={handleVerlagChange}
              input={<OutlinedInput id="select-multiple-verlag-chip" label="Verlag" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {verlags.map((verlag) => (
                <MenuItem key={verlag.name} value={verlag.name}>
                  {verlag.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="theme-select-label">Thema</InputLabel>
            <Select
              labelId="theme-select-label"
              id="theme-select"
              multiple
              value={getSelectedThemes()}
              onChange={handleThemeChange}
              input={<OutlinedInput id="select-multiple-theme-chip" label="Thema" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Array.isArray(themes) &&
                themes.map((theme) => (
                  <MenuItem key={theme.name} value={theme.name}>
                    {theme.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="aggregation-select-label">Zeitraumdarstellung</InputLabel>
            <Select
              labelId="aggregation-select-label"
              id="aggregation-select"
              value={filterState.vnrAggregation}
              onChange={(event) =>
                setFilterState((prev) => ({
                  ...prev,
                  vnrAggregation: event.target.value as 'auto' | 'daily' | 'weekly' | 'monthly',
                }))
              }
              label="Zeitraumdarstellung"
            >
              <MenuItem value="auto">Automatisch</MenuItem>
              <MenuItem value="daily">Täglich</MenuItem>
              <MenuItem value="weekly">Wöchentlich</MenuItem>
              <MenuItem value="monthly">Monatlich</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="40px" mt="40px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          ROI 30 Übersicht (letzte 6 Monate)
        </Typography>
        <Box mt="-20px">
          <RoiNetworkMonthlySummary />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="40px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach Jahr/Monat
        </Typography>
        <Box height="250px" mt="-20px">
          <RoiPerMonth />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="40px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Revenue Generation Timeline
        </Typography>
        <Box height="400px" mt="-20px">
          <RevenueTimeline />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="40px" mt="40px" mb="40px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          ANFO Generation Timeline
        </Typography>
        <Box height="400px" mt="-20px">
          <AnfoTimeline />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="40px" mt="40px" mb="40px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          DOI Rate Timeline
        </Typography>
        <Box height="400px" mt="-20px">
          <DoiTimeline />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="40px" mt="40px" mb="40px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Anfos pro NL (ohne Tripwire)
        </Typography>
        <Box height="400px" mt="-20px">
          <AnfoPerNewsletter />
        </Box>
      </Box>
      {getSelectedNetworks().length > 1 && (
        <Box display="grid" gridTemplateColumns="1fr" gap="40px">
          <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
            Netzwerk-Vergleich
          </Typography>
          <Box mt="-20px">
            <RoiPerNetwork />
          </Box>
        </Box>
      )}
      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          ROI nach Market Cap (Einzel-Analysen-Leads)
        </Typography>
        <Box mt="-20px">
          <RoiPerMarketCap />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach Kundenstatus
        </Typography>
        <Box mt="-20px">
          <RoiPerCustomerStatus />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach Planungsbereich & Newsletter
        </Typography>
        <Box mt="-20px">
          <PlanungsbereichNewsletter />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach Verlag
        </Typography>
        <Box mt="-20px">
          <RoiPerVerlag />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="20px" mt="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach Thema
        </Typography>
        <Box mt="-20px">
          <RoiPerTheme />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach E-Mail Domain
        </Typography>
        <Box mt="-20px">
          <RoiPerEmailDomain />
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          Gruppierung nach Wochentag
        </Typography>
        <Box mt="-20px">
          <RoiPerWeekday />
        </Box>
      </Box>
      {(getNetworkDisplayCondition() === 'finanztrends' ||
        getNetworkDisplayCondition() === 'mixed') && (
        <>
          <Box display="grid" gridTemplateColumns="1fr" gap="20px">
            <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
              Gruppierung nach Kanal/Quelle ( für FIT )
            </Typography>
            <Box mt="-20px">
              <RoiPerSource />
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns="1fr" gap="20px">
            <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
              Gruppierung nach E-Mail Provider ( für FIT )
            </Typography>
            <Box mt="-20px">
              <RoiPerEmailProvider />
            </Box>
          </Box>
        </>
      )}
      {(getNetworkDisplayCondition() === 'other' || getNetworkDisplayCondition() === 'mixed') && (
        <>
          <Box display="grid" gridTemplateColumns="1fr" gap="20px">
            <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
              Gruppierung nach Asset/Aktie
            </Typography>
            <Box mt="-20px">
              <RoiPerAsset />
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns="1fr" gap="20px">
            <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
              Gruppierung nach Ländercode (ISIN)
            </Typography>
            <Box mt="-20px">
              <RoiPerCountry />
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns="1fr" gap="20px">
            <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
              Gruppierung nach Asset Type
            </Typography>
            <Box mt="-20px">
              <RoiPerAssetType />
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns="1fr" gap="20px">
            <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
              Gruppierung nach Asset Sector
            </Typography>
            <Box mt="-20px">
              <RoiPerAssetSector />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RoiEvaluationPage;
